package com.schoolconnect.app.feature.schoolconfig

import com.schoolconnect.app.feature.common.PaginationPolicy

data class SchoolConfigRow(
    val id: String,
    val schoolName: String,
    val code: String,
    val city: String,
    val isActive: Boolean,
    val updatedDate: String
)

data class SchoolConfigUiState(
    val searchQuery: String = "",
    val statusFilter: SchoolConfigStatusFilter = SchoolConfigStatusFilter.ALL,
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val rows: List<SchoolConfigRow> = emptyList(),
    val totalCount: Int = 0,
    val sortField: SchoolConfigSortField = SchoolConfigSortField.SCHOOL_NAME,
    val sortAscending: Boolean = true,
    val isLoading: Boolean = false,
    val errorMessage: String = "",
    val feedbackMessage: String = "",
    val editingRow: SchoolConfigRow? = null,
    val editingConfig: SchoolConfigEditData? = null,
    val viewingRow: SchoolConfigRow? = null,
    val viewingConfig: SchoolConfigEditData? = null,
    val isSaving: Boolean = false
)

data class SchoolConfigEditData(
    val academicYear: String,
    val academicYearId: String? = null,
    val academicYears: List<AcademicYearOption> = emptyList(),
    val weeklyOffDay: String = "Sunday",
    val attendance: List<ThresholdDraft>,
    val exams: List<ThresholdDraft>,
    val holidays: List<HolidayDraft>,
    val features: List<FeatureDraft>,
    val weeklyHolidayApiAvailable: Boolean = false
)

data class AcademicYearOption(val id: String, val label: String, val isCurrent: Boolean = false)

data class HolidayDraft(val date: String, val name: String)
data class ThresholdDraft(val level: String, val minimum: String, val maximum: String, val grade: String = "")
data class FeatureDraft(val key: String, val enabled: Boolean)

sealed interface SchoolConfigUiEvent {
    data class SearchChanged(val query: String) : SchoolConfigUiEvent
    data class StatusFilterChanged(val status: SchoolConfigStatusFilter) : SchoolConfigUiEvent
    data object FiltersReset : SchoolConfigUiEvent
    data class PageChanged(val pageIndex: Int) : SchoolConfigUiEvent
    data class PageSizeChanged(val pageSize: Int) : SchoolConfigUiEvent
    data class SortChanged(val field: SchoolConfigSortField) : SchoolConfigUiEvent
    data class ViewClicked(val row: SchoolConfigRow) : SchoolConfigUiEvent
    data object ViewDismissed : SchoolConfigUiEvent
    data class EditClicked(val row: SchoolConfigRow) : SchoolConfigUiEvent
    data class AcademicYearSelected(val id: String, val label: String) : SchoolConfigUiEvent
    data object EditDismissed : SchoolConfigUiEvent
    data class EditSaved(val draft: SchoolConfigEditData) : SchoolConfigUiEvent
    data object HolidayTemplateRequested : SchoolConfigUiEvent
    data class HolidayImportRequested(val fileName: String, val bytes: ByteArray) : SchoolConfigUiEvent
    data class OperationFailed(val message: String) : SchoolConfigUiEvent
    data object FeedbackDismissed : SchoolConfigUiEvent
    data object RetryRequested : SchoolConfigUiEvent
}

enum class SchoolConfigSortField(val apiField: String) {
    SCHOOL_NAME("school_name"),
    CODE("code"),
    CITY("city"),
    UPDATED_DATE("updated_at"),
    STATUS("is_active")
}

enum class SchoolConfigStatusFilter {
    ALL,
    ACTIVE,
    INACTIVE
}

internal fun SchoolConfigStatusFilter.displayLabel(): String = when (this) {
    SchoolConfigStatusFilter.ALL -> "All"
    SchoolConfigStatusFilter.ACTIVE -> "Active"
    SchoolConfigStatusFilter.INACTIVE -> "Inactive"
}

internal fun String.toSchoolConfigStatusFilter(): SchoolConfigStatusFilter = when (this) {
    "Active" -> SchoolConfigStatusFilter.ACTIVE
    "Inactive" -> SchoolConfigStatusFilter.INACTIVE
    else -> SchoolConfigStatusFilter.ALL
}

internal fun SchoolConfigStatusFilter.toIsActiveParam(): Boolean? = when (this) {
    SchoolConfigStatusFilter.ALL -> null
    SchoolConfigStatusFilter.ACTIVE -> true
    SchoolConfigStatusFilter.INACTIVE -> false
}
